import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Check, CircleCheck, Loader2, MapPin, Code2, X } from "lucide-react";

import { useAuthor } from "@/hooks/useAuthor";
import { genUserName } from "@/lib/genUserName";
import { cn } from "@/lib/utils";
import type { InboxRequest } from "@/hooks/useModeration";

const CONFIRM_LABELS: Record<string, { label: string; icon: typeof MapPin }> = {
  kenya: { label: "Kenya-based", icon: MapPin },
  woman: { label: "Woman", icon: CircleCheck },
  oss: { label: "Open-source builder", icon: Code2 },
};

function timeAgo(sec: number): string {
  const diff = Date.now() / 1000 - sec;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function RequestCard({
  request,
  onApprove,
  onDecline,
  approving,
  declining,
}: {
  request: InboxRequest;
  onApprove: () => void;
  onDecline: (message: string) => void;
  approving: boolean;
  declining: boolean;
}) {
  const author = useAuthor(request.applicant);
  const metadata = author.data?.metadata;
  const displayName =
    request.request.name ||
    metadata?.name ||
    metadata?.display_name ||
    genUserName(request.applicant);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [message, setMessage] = useState(
    "Thank you for applying to the chama. It's not the right fit right now — you're welcome to reapply later.",
  );
  const reviewed = request.reviewed;

  return (
    <Card className={cn("shadow-sm", reviewed && "opacity-70")}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="size-11 border">
              <AvatarImage src={metadata?.picture} alt={displayName} />
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {displayName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-semibold truncate">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">
                {metadata?.nip05 ?? `${request.applicant.slice(0, 16)}…`} ·{" "}
                {timeAgo(request.createdAt)}
              </p>
            </div>
          </div>
          {reviewed && (
            <Badge variant={reviewed.status === "approved" ? "default" : "secondary"}>
              {reviewed.status}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-1.5">
          {request.request.confirmations.map((c) => {
            const conf = CONFIRM_LABELS[c];
            if (!conf) return null;
            return (
              <Badge key={c} variant="outline" className="gap-1">
                <conf.icon className="size-3" />
                {conf.label}
              </Badge>
            );
          })}
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <p className="font-medium mb-1">Who they are</p>
            <p className="text-muted-foreground whitespace-pre-wrap">{request.request.about}</p>
          </div>
          <div>
            <p className="font-medium mb-1">Why they want to join</p>
            <p className="text-muted-foreground whitespace-pre-wrap">{request.request.why}</p>
          </div>
          {request.request.links && (
            <div>
              <p className="font-medium mb-1">Links</p>
              <p className="text-muted-foreground break-words">{request.request.links}</p>
            </div>
          )}
        </div>

        {!reviewed && (
          <div className="space-y-3 border-t pt-4">
            {declineOpen ? (
              <div className="space-y-2">
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  aria-label="Decline message"
                />
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={declining}
                    onClick={() => onDecline(message)}
                  >
                    {declining ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
                    Send decline
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeclineOpen(false)}>
                    Back
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  disabled={approving}
                  onClick={onApprove}
                >
                  {approving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                  Approve &amp; send invite
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDeclineOpen(true)}>
                  <X className="size-4" />
                  Decline
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
